import { GetStaticPropsContext, GetStaticPropsResult, NextPage } from "next";
import { useRouter } from "next/router";
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { match } from "ts-pattern";

import DatabaseTab from "~/components/category/[id]/database/DatabaseTab";
import CategoryHeader from "~/components/category/[id]/header/CategoryHeader";
import LimitedTab from "~/components/category/[id]/limited/LimitedTab";
import QuestTab from "~/components/category/[id]/quest/QuestTab";
import {
  PageTab,
  Props,
  isPageTab,
} from "~/components/category/[id]/quest/type";
import PageHead from "~/components/common/PageHead";
import { Layout } from "~/lib/client/layout";
import { useActivityLogger } from "~/lib/client/logger/useActivityLogger";
import { createGraphqlClient } from "~/lib/graphql/graphqlClient";

export async function getStaticPaths() {
  const client = createGraphqlClient();
  const { categories } = await client.categoryDetailsForGsp();

  return {
    paths: categories.map((c) => ({ params: { id: c.id.toString() } })),
    fallback: "blocking",
  };
}

export async function getStaticProps(
  context: GetStaticPropsContext
): Promise<GetStaticPropsResult<Props>> {
  const id = context.params?.id;
  if (typeof id !== "string") {
    return {
      notFound: true,
    };
  }

  if (Number(id) == 0) {
    return {
      notFound: true,
    };
  }

  const graphqlClient = createGraphqlClient();
  const { category } = await graphqlClient.categoryDetail({ id: Number(id) });

  return {
    props: {
      category: {
        ...category,
        membershipLimitedContents: category.membershipLimitedContents.filter(
          (c) => c.hideAt == null
        ),
      },
      pageTab: null,
    },
    revalidate: 60 * 10, // 10 min
  };
}

export const CategoryDetailContext = createContext(
  {} as {
    props: Props;
    selectedTab: PageTab;
    setSelectedTab: Dispatch<SetStateAction<PageTab>>;
  }
);

export const useCategoryDetailContext = () => useContext(CategoryDetailContext);

const CategoryDetailPage: NextPage<Props> = (props) => {
  const sendActivityLogger = useActivityLogger();
  const router = useRouter();
  const pageTab = ((tabQuery: string | string[] | undefined): PageTab => {
    if (tabQuery == undefined) {
      return "QUEST";
    }

    if (Array.isArray(tabQuery)) {
      return "QUEST";
    }

    const tab = tabQuery.toUpperCase();
    if (isPageTab(tab)) {
      return tab;
    }
    return "QUEST";
  })(router.query.tab || "QUEST");
  const [selectedTab, setSelectedTab] = useState<PageTab>(pageTab);
  const { category } = props;
  const Head = () => (
    <PageHead
      title={`${category.name} | FUSSY`}
      ogDescription={`FUSSY で"${category.name}"を応援しよう`}
      ogUrl={`/category/${category.id}`}
    />
  );

  useEffect(() => {
    if (sendActivityLogger === undefined) return;

    (async () => {
      await sendActivityLogger({
        pageName: "categoryDetail",
        actionName: "show",
      });
    })();
  }, [sendActivityLogger]);

  useEffect(() => {
    setSelectedTab(pageTab);
  }, [pageTab]);

  return (
    <CategoryDetailContext.Provider
      value={{ selectedTab, setSelectedTab, props }}
    >
      <Head />
      <Layout layoutType={"YTLayout"}>
        <main>
          <CategoryHeader />
          {match(selectedTab)
            .with("DATABASE", () => <DatabaseTab />)
            .with("QUEST", () => <QuestTab />)
            .with("LIMITED", () => <LimitedTab />)
            .exhaustive()}
        </main>
      </Layout>
    </CategoryDetailContext.Provider>
  );
};

export default CategoryDetailPage;
